import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Info, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M11 · Démarrage en côte (2 méthodes)
// Œuvre pédagogique interactive :
//  · Toggle « Frein à main » (méthode examen) vs « Frein au pied » (technique avancée)
//  · 5 à 6 étapes séquentielles avec pédales animées (embrayage / frein / accélérateur)
//  · Frein à main : levier en position VISIBLE (serré/desserré)
//  · Voiture qui GLISSE VERS LE BAS si erreur de séquence
//  · Jauge de tr/min qui monte au point de patinage
//  · Panneau : action courante + timing + piège
// ══════════════════════════════════════════════════════════════════════════

const SEQUENCES = {
  freinamain: {
    label: 'Frein à main (méthode examen)',
    color: '#22c55e',
    steps: [
      { id: 'fa',   action: "1. Frein à main SERRÉ",           embr: 0, frein: 0, gaz: 0, fa: 1, rpm: 800,  vitesse: 0, recule: false, tip: "Assure-toi que la voiture est bien immobile. Frein à main tiré à fond." },
      { id: 'deb',  action: "2. Débrayer + 1re",               embr: 1, frein: 0, gaz: 0, fa: 1, rpm: 800,  vitesse: 0, recule: false, tip: "Pied gauche à FOND sur l'embrayage. Levier en 1re." },
      { id: 'gaz',  action: "3. Gaz doux ~1500 tr/min",        embr: 1, frein: 0, gaz: 0.4, fa: 1, rpm: 1500, vitesse: 0, recule: false, tip: "Accélérateur légèrement enfoncé. Le moteur monte à ~1500 tr/min." },
      { id: 'pdp',  action: "4. Trouver le point de patinage", embr: 0.6, frein: 0, gaz: 0.5, fa: 1, rpm: 1300, vitesse: 0, recule: false, tip: "Relâcher DOUCEMENT l'embrayage jusqu'à sentir le moteur qui « prend »." },
      { id: 'lib',  action: "5. Relâcher frein à main",        embr: 0.5, frein: 0, gaz: 0.5, fa: 0, rpm: 1400, vitesse: 3, recule: false, tip: "PUIS libérer le frein à main. Si le point de patinage est bon, la voiture NE RECULE PAS." },
      { id: 'go',   action: "6. Rouler",                       embr: 0, frein: 0, gaz: 0.6, fa: 0, rpm: 2000, vitesse: 15, recule: false, tip: "Embrayage complètement relâché. Accélération franche. Bravo !" },
    ],
  },
  freinpied: {
    label: 'Frein au pied (technique avancée)',
    color: '#f59e0b',
    steps: [
      { id: 'fr',   action: "1. Frein AU PIED enfoncé",       embr: 0, frein: 1, gaz: 0, fa: 0, rpm: 800,  vitesse: 0, recule: false, tip: "Pied droit sur la pédale de frein. Voiture immobile en côte." },
      { id: 'deb',  action: "2. Débrayer + 1re",              embr: 1, frein: 1, gaz: 0, fa: 0, rpm: 800,  vitesse: 0, recule: false, tip: "Pied gauche débraye, main droite en 1re. Pied droit MAINTIENT le frein." },
      { id: 'pdp',  action: "3. Point de patinage (frein maintenu)", embr: 0.5, frein: 1, gaz: 0, fa: 0, rpm: 900,  vitesse: 0, recule: false, tip: "Relâcher DOUCEMENT l'embrayage jusqu'au point de patinage. Frein TOUJOURS enfoncé." },
      { id: 'bas', action: "4. Bascule frein → gaz (< 0.3 s)", embr: 0.4, frein: 0, gaz: 0.5, fa: 0, rpm: 1600, vitesse: 2, recule: false, tip: "Bascule ULTRA rapide du pied droit : frein → accélérateur. Le point de patinage retient la voiture." },
      { id: 'go',  action: "5. Rouler",                        embr: 0, frein: 0, gaz: 0.6, fa: 0, rpm: 2200, vitesse: 15, recule: false, tip: "Embrayage relâché, gaz progressif. En côte forte, préfère la méthode frein à main." },
    ],
  },
};

// Pédale animée : niveau 0=relâchée, 1=enfoncée à fond
function Pedale({ x, y, label, level, color }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <text y={-6} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace" fontWeight="bold">{label}</text>
      <rect x={-16} y={0} width={32} height={54} rx={6} fill="#1e293b" stroke="#334155" strokeWidth={1.5} />
      {/* Course visible */}
      <rect x={-13} y={3} width={26} height={48} rx={3} fill="#0b1120" />
      {/* Curseur qui descend selon level */}
      <rect x={-13} y={3 + level * 44} width={26} height={4} rx={2} fill={color} style={{ transition: 'y 0.5s' }} />
      <text y={35} textAnchor="middle" fill={level > 0.5 ? color : '#475569'} fontSize={16} fontWeight="900">
        {Math.round(level * 100)}
      </text>
      <text y={68} textAnchor="middle" fill="#64748b" fontSize={8}>%</text>
    </g>
  );
}

// Frein à main visuel (levier vertical)
function FreinAMain({ x, y, engaged }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <text y={-6} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace" fontWeight="bold">FREIN À MAIN</text>
      <rect x={-20} y={0} width={40} height={54} rx={6} fill="#1e293b" stroke="#334155" strokeWidth={1.5} />
      <g style={{ transition: 'transform 0.6s', transform: engaged ? 'rotate(0deg)' : 'rotate(-30deg)', transformOrigin: '0 45px' }}>
        <rect x={-4} y={5} width={8} height={40} rx={2} fill="#525b6b" />
        <circle cx={0} cy={5} r={8} fill={engaged ? '#dc2626' : '#71717a'} stroke="#0b1120" strokeWidth={1.5} />
      </g>
      <text y={68} textAnchor="middle" fill={engaged ? '#dc2626' : '#22c55e'} fontSize={9} fontFamily="monospace" fontWeight="bold">
        {engaged ? 'SERRÉ' : 'RELÂCHÉ'}
      </text>
    </g>
  );
}

export default function DemarrageCoteSvg() {
  const [method, setMethod] = useState('freinamain');
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [errorRoll, setErrorRoll] = useState(false); // état d'erreur "voiture recule"
  const timerRef = useRef(null);

  const seq = SEQUENCES[method];
  const step = seq.steps[idx];

  useEffect(() => {
    if (!playing) return undefined;
    timerRef.current = setInterval(() => {
      setIdx((i) => {
        if (i >= seq.steps.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, 1800);
    return () => clearInterval(timerRef.current);
  }, [playing, seq.steps.length]);

  useEffect(() => { setIdx(0); setPlaying(false); setErrorRoll(false); }, [method]);

  const reset = () => { setIdx(0); setPlaying(false); setErrorRoll(false); };
  const play = () => { if (idx >= seq.steps.length - 1) setIdx(0); setPlaying(true); };
  const next = () => setIdx((i) => Math.min(seq.steps.length - 1, i + 1));

  // Simulation d'erreur : si on relâche frein à main AVANT point de patinage
  const triggerError = () => {
    setPlaying(false);
    setErrorRoll(true);
    setTimeout(() => setErrorRoll(false), 3000);
  };

  // Position de la voiture sur la côte (0 = bas, 100 = haut) — animée par CSS transition
  const carPos = errorRoll ? 0 : 30 + (step.vitesse * 2.5);

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
          {Object.entries(SEQUENCES).map(([k, s], i) => (
            <button key={k} onClick={() => setMethod(k)} data-testid={`m11-method-${k}`}
              className={`h-9 px-3.5 text-[11.5px] font-bold font-mono-spec ${method === k ? 'text-white' : 'text-white/60'} ${i > 0 ? 'border-l border-white/15' : ''}`}
              style={method === k ? { background: s.color + '25' } : {}}
            >
              {s.label}
            </button>
          ))}
        </div>
        {!playing ? (
          <button onClick={play} data-testid="m11-play" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-500 text-emerald-950 font-bold text-[12px] hover:bg-emerald-400">
            <Play size={13} /> Jouer
          </button>
        ) : (
          <button onClick={() => setPlaying(false)} data-testid="m11-pause" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-500 text-amber-950 font-bold text-[12px]">
            <Pause size={13} /> Pause
          </button>
        )}
        <button onClick={next} disabled={idx >= seq.steps.length - 1} data-testid="m11-next" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-[12px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
          Étape <ChevronRight size={12} />
        </button>
        {method === 'freinamain' && (
          <button onClick={triggerError} data-testid="m11-error" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-red-500/50 text-red-300 hover:bg-red-500/10 text-[11px] font-semibold">
            <AlertTriangle size={12} /> Simuler erreur
          </button>
        )}
        <button onClick={reset} data-testid="m11-reset" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold">
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Démarrage en côte">
            <defs>
              <linearGradient id="m11-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1e293b" />
              </linearGradient>
              <linearGradient id="m11-grass" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4d7c3a" />
                <stop offset="100%" stopColor="#365828" />
              </linearGradient>
            </defs>

            {/* Ciel dégradé */}
            <rect x={0} y={0} width={800} height={520} fill="#0f2537" />
            <rect x={0} y={0} width={800} height={520} fill="#1e3a5f" opacity={0.5} />
            {/* Soleil */}
            <circle cx={650} cy={80} r={26} fill="#fef3c7" opacity={0.9} />
            <circle cx={650} cy={80} r={42} fill="#fbbf24" opacity={0.18} />
            {/* Nuages */}
            <g opacity={0.7}>
              <ellipse cx={150} cy={110} rx={38} ry={10} fill="#cbd5e1" />
              <ellipse cx={180} cy={100} rx={22} ry={8} fill="#e2e8f0" />
              <ellipse cx={430} cy={70} rx={30} ry={9} fill="#cbd5e1" />
            </g>

            {/* Herbe (colline en profil, montée vers la droite) */}
            <path d="M 0 500 L 800 200 L 800 700 L 0 700 Z" fill="url(#m11-grass)" />
            {/* Route bitume sur la pente (bande plus foncée) */}
            <path d="M 0 570 L 800 270 L 800 320 L 0 620 Z" fill="url(#m11-road)" />
            {/* Bordures blanches de la route */}
            <line x1={0} y1={570} x2={800} y2={270} stroke="#e2e8f0" strokeWidth={2.5} opacity={0.7} />
            <line x1={0} y1={620} x2={800} y2={320} stroke="#e2e8f0" strokeWidth={2.5} opacity={0.7} />
            {/* Ligne médiane pointillée au centre de la route */}
            <line x1={0} y1={595} x2={800} y2={295} stroke="#fbbf24" strokeWidth={3} strokeDasharray="22 15" opacity={0.85} />

            {/* Petits éléments décor (herbe latérale, arbres profil) */}
            <g opacity={0.85}>
              <g transform="translate(200 490) rotate(-20.55)">
                <rect x={-2} y={-30} width={4} height={30} fill="#3f2b1a" />
                <circle cx={0} cy={-38} r={16} fill="#22a55a" />
                <circle cx={-8} cy={-32} r={10} fill="#16a34a" />
                <circle cx={8} cy={-32} r={10} fill="#16a34a" />
              </g>
              <g transform="translate(560 380) rotate(-20.55)">
                <rect x={-2} y={-30} width={4} height={30} fill="#3f2b1a" />
                <circle cx={0} cy={-38} r={16} fill="#22a55a" />
                <circle cx={-8} cy={-32} r={10} fill="#16a34a" />
                <circle cx={8} cy={-32} r={10} fill="#16a34a" />
              </g>
            </g>

            {/* Panneau pente 10% (au sol) */}
            <g transform="translate(80 640)">
              <polygon points="0,-32 34,-32 34,0 0,0" fill="#fef3c7" stroke="#dc2626" strokeWidth={2.5} />
              <text x={17} y={-12} textAnchor="middle" fill="#0b1120" fontSize={11} fontWeight="900" fontFamily="monospace">10 %</text>
              <text x={17} y={-2} textAnchor="middle" fill="#0b1120" fontSize={7} fontWeight="bold" fontFamily="monospace">côte</text>
            </g>
            {/* Repère « départ » et « arrivée » */}
            <g>
              <line x1={280} y1={510} x2={280} y2={640} stroke="#fbbf24" strokeWidth={2} strokeDasharray="4 4" opacity={0.5} />
              <text x={280} y={655} textAnchor="middle" fill="#fbbf24" fontSize={10} fontFamily="monospace" fontWeight="bold">DÉPART</text>
              <line x1={640} y1={370} x2={640} y2={500} stroke="#22c55e" strokeWidth={2} strokeDasharray="4 4" opacity={0.5} />
              <text x={640} y={515} textAnchor="middle" fill="#22c55e" fontSize={10} fontFamily="monospace" fontWeight="bold">SOMMET ✓</text>
            </g>

            {/* VOITURE EGO — vue de côté (side profile), position variable */}
            {(() => {
              // Position le long de la pente : x va de 100 à 700
              const t = Math.max(0, Math.min(1, carPos / 100));
              const cx = 100 + t * 500;
              const cy = 570 - t * 260 - 15; // suit la pente (offset pour poser la voiture au dessus de la route)
              return (
                <g style={{ transition: `transform ${errorRoll ? '2.6s cubic-bezier(0.5, 0, 0.8, 1)' : '1s cubic-bezier(0.4, 0, 0.2, 1)'}` }} transform={`translate(${cx} ${cy}) rotate(-20.55)`}>
                  {/* Alertes */}
                  {errorRoll && (
                    <g transform="translate(0 -68)">
                      <rect x={-52} y={-14} width={104} height={22} rx={10} fill="#dc2626" />
                      <text y={2} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="900">⚠ RECULE !</text>
                    </g>
                  )}
                  {!errorRoll && step.vitesse >= 3 && (
                    <g transform="translate(0 -60)">
                      <rect x={-40} y={-13} width={80} height={20} rx={10} fill="#22c55e" />
                      <text y={2} textAnchor="middle" fill="#052e16" fontSize={11} fontWeight="900">✓ MONTE</text>
                    </g>
                  )}

                  {/* Ombre */}
                  <ellipse cx={0} cy={30} rx={58} ry={5} fill="#000" opacity={0.4} />

                  {/* Roues (2 cercles visibles en vue de côté) */}
                  <g>
                    {/* Roue arrière (gauche) */}
                    <circle cx={-38} cy={16} r={15} fill="#18181b" stroke="#0b1120" strokeWidth={2} />
                    <circle cx={-38} cy={16} r={8} fill="#71717a" />
                    <line x1={-38} y1={9} x2={-38} y2={23} stroke="#3f3f46" strokeWidth={1.5} />
                    <line x1={-45} y1={16} x2={-31} y2={16} stroke="#3f3f46" strokeWidth={1.5} />
                    {/* Roue avant (droite) */}
                    <circle cx={38} cy={16} r={15} fill="#18181b" stroke="#0b1120" strokeWidth={2} />
                    <circle cx={38} cy={16} r={8} fill="#71717a" />
                    <line x1={38} y1={9} x2={38} y2={23} stroke="#3f3f46" strokeWidth={1.5} />
                    <line x1={31} y1={16} x2={45} y2={16} stroke="#3f3f46" strokeWidth={1.5} />
                  </g>

                  {/* Corps carrosserie (silhouette latérale) */}
                  <path d="M -54 14 L -50 -2 L -32 -18 L 14 -22 L 34 -14 L 54 -4 L 54 14 Z" fill="#dc2626" stroke="#0b1120" strokeWidth={2.2} />
                  {/* Ligne de séparation vitre/carrosserie */}
                  <line x1={-46} y1={-2} x2={50} y2={-4} stroke="#0b1120" strokeWidth={1.5} opacity={0.5} />

                  {/* Pare-brise avant (côté droite) */}
                  <path d="M 14 -22 L 34 -14 L 34 -6 L 16 -6 Z" fill="#0ea5e9" opacity={0.6} />
                  {/* Vitre latérale */}
                  <path d="M -30 -18 L 12 -22 L 12 -6 L -30 -6 Z" fill="#0ea5e9" opacity={0.55} />
                  {/* Montant central portière */}
                  <line x1={-8} y1={-20} x2={-8} y2={-6} stroke="#0b1120" strokeWidth={2} />

                  {/* Poignée + rétro */}
                  <rect x={-20} y={-1} width={16} height={2.5} rx={1} fill="#7f1d1d" />
                  <path d="M 34 -12 L 42 -10 L 40 -6 L 34 -8 Z" fill="#0b1120" />

                  {/* Phare avant (jaune) */}
                  <circle cx={53} cy={-2} r={3.5} fill="#fef3c7" />
                  {/* Feu arrière (rouge) — allumé si frein actif */}
                  {step.frein > 0 ? (
                    <circle cx={-53} cy={-2} r={3.5} fill="#dc2626">
                      <animate attributeName="opacity" values="1;0.5;1" dur="0.8s" repeatCount="indefinite" />
                    </circle>
                  ) : (
                    <circle cx={-53} cy={-2} r={3.5} fill="#7f1d1d" />
                  )}

                  {/* Fumée d'échappement quand moteur tourne fort */}
                  {step.rpm >= 1400 && !errorRoll && (
                    <g>
                      <circle cx={-60} cy={12} r={4} fill="#94a3b8" opacity={0.6}>
                        <animate attributeName="opacity" values="0.6;0;0.6" dur="0.6s" repeatCount="indefinite" />
                        <animate attributeName="cx" values="-60;-74;-60" dur="0.6s" repeatCount="indefinite" />
                      </circle>
                      <circle cx={-68} cy={9} r={3} fill="#cbd5e1" opacity={0.5}>
                        <animate attributeName="opacity" values="0.5;0;0.5" dur="0.7s" repeatCount="indefinite" />
                      </circle>
                    </g>
                  )}

                  {/* Indicateur frein à main (au-dessus du toit) */}
                  {step.fa === 1 && (
                    <g transform="translate(0 -38)">
                      <rect x={-18} y={-10} width={36} height={16} rx={6} fill="#0b1120" stroke="#22c55e" strokeWidth={1.5} />
                      <text y={2} textAnchor="middle" fill="#22c55e" fontSize={10} fontWeight="900" fontFamily="monospace">🅿️ FA</text>
                    </g>
                  )}

                  {/* Étiquette MOI (sur le toit) */}
                  <text x={0} y={-14} textAnchor="middle" fontSize={9} fontWeight="900" fill="#fff" opacity={0.85}>MOI</text>
                </g>
              );
            })()}

            {/* Indicateur pente (à supprimer — remplacé par le panneau) */}
            <g transform="translate(140 640)">
              <text y={16} fill="#94a3b8" fontSize={10} fontFamily="monospace">Côte modérée en agglomération</text>
            </g>

            {/* PANNEAU PÉDALES + FREIN À MAIN */}
            <g transform="translate(60 100)">
              <rect x={-20} y={-30} width={380} height={140} rx={12} fill="#0f172a" stroke="#334155" strokeWidth={2} />
              <text y={-14} fill="#94a3b8" fontSize={11} fontFamily="monospace" fontWeight="bold">ÉTAT DES COMMANDES · TEMPS RÉEL</text>

              {/* Pédale embrayage (gauche) — pied G */}
              <Pedale x={30} y={8} label="EMBRAY. (G)" level={step.embr} color="#8b5cf6" />
              {/* Pédale frein — pied D */}
              <Pedale x={110} y={8} label="FREIN (D)" level={step.frein} color="#dc2626" />
              {/* Pédale accélérateur — pied D */}
              <Pedale x={190} y={8} label="GAZ (D)" level={step.gaz} color="#22c55e" />

              {/* Frein à main */}
              <FreinAMain x={280} y={8} engaged={step.fa === 1} />
            </g>

            {/* JAUGE MOTEUR (tr/min) */}
            <g transform="translate(560 160)">
              <circle r={70} fill="#0b1120" stroke="#334155" strokeWidth={2.5} />
              <circle r={60} fill="none" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" opacity={0.4} />
              <g style={{ transition: 'transform 0.6s' }} transform={`rotate(${-135 + (step.rpm / 3500) * 270})`}>
                <line x1={0} y1={0} x2={0} y2={-56} stroke="#f59e0b" strokeWidth={3.5} />
                <circle r={6} fill="#f59e0b" />
              </g>
              <text y={26} textAnchor="middle" fill="#fff" fontSize={18} fontWeight="900" fontFamily="monospace">{step.rpm}</text>
              <text y={40} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">tr/min</text>
              {step.rpm >= 1400 && step.rpm <= 1700 && (
                <text y={-80} textAnchor="middle" fill="#22c55e" fontSize={10} fontWeight="bold">✓ ZONE PATINAGE</text>
              )}
            </g>

            {/* TIMELINE DES ÉTAPES */}
            <g transform="translate(60 500)">
              {seq.steps.map((s, i) => (
                <g key={s.id} transform={`translate(${i * (680 / seq.steps.length)} 0)`}>
                  <circle cx={16} cy={16} r={14} fill={i === idx ? seq.color : i < idx ? seq.color + '90' : '#334155'} stroke={i === idx ? '#fff' : 'none'} strokeWidth={2} />
                  <text x={16} y={20} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">{i + 1}</text>
                  {i < seq.steps.length - 1 && (
                    <line x1={32} y1={16} x2={680 / seq.steps.length - 8} y2={16} stroke={i < idx ? seq.color : '#334155'} strokeWidth={2} />
                  )}
                </g>
              ))}
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">M11 · Démarrage en côte · {seq.label}</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1d — Maîtriser les démarrages en pente</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m11-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: seq.color, background: seq.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: seq.color }}>
              Étape {idx + 1} / {seq.steps.length}
            </div>
            <div className="mt-1 font-serif-editorial text-white text-[18px] leading-tight">{step.action}</div>
          </div>

          {errorRoll && (
            <div className="mt-3 rounded-lg border-2 border-red-500 bg-red-950/40 p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-300 mt-0.5 shrink-0 animate-pulse" />
              <div>
                <div className="text-red-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold">⚠ Voiture qui recule !</div>
                <p className="mt-1 text-white/90 text-[12.5px] leading-relaxed">
                  Tu as relâché le frein à main AVANT d&apos;atteindre le point de patinage. La voiture glisse dans la pente. À l&apos;examen : ÉLIMINATOIRE.
                </p>
              </div>
            </div>
          )}

          <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Consigne</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{step.tip}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <div className="text-white/50 font-mono-spec uppercase tracking-wider font-bold">Régime</div>
              <div className="mt-0.5 font-mono-spec text-white font-bold text-[14px]">{step.rpm} tr/min</div>
            </div>
            <div>
              <div className="text-white/50 font-mono-spec uppercase tracking-wider font-bold">Vitesse</div>
              <div className="mt-0.5 font-mono-spec text-white font-bold text-[14px]">{step.vitesse} km/h</div>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Astuce examen</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">
                {method === 'freinamain'
                  ? "La méthode FREIN À MAIN est celle attendue à l'examen. Elle garantit ZÉRO recul et laisse le temps de trouver le point de patinage sereinement."
                  : "Réserver la méthode FREIN AU PIED aux pentes très légères (<3%). Bascule frein→gaz en <0,3 s. En pente forte, préférer le frein à main."}
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Erreur classique</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">
                {method === 'freinamain'
                  ? "Relâcher le frein à main AVANT le point de patinage. Clique sur « Simuler erreur » pour voir la voiture reculer."
                  : "Bascule trop lente frein→gaz = voiture qui recule + calage moteur assuré. Reste sur le frein à main si tu doutes."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
